class TabCollectionCarousel extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll('.tab-collection-carousel-tab');
    this.panels = this.querySelectorAll('.tab-collection-carousel-panel');
    this.tabLinks = this.querySelectorAll('[data-tab-link]');
    this.groups = this.querySelectorAll('.tab-collection-carousel-group');
    // Map<panelElement, SwiperInstance> - only holds panels that have been inited
    this.swipers = new Map();
    this._onTabClick = this._onTabClick.bind(this);
    this._onGroupClick = this._onGroupClick.bind(this);
    this._onBlockSelect = this._onBlockSelect.bind(this);
  }
 
  connectedCallback() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this._onTabClick);
    });
    this.groups.forEach((group) => {
      group.addEventListener('click', this._onGroupClick);
    });
 
    // Apply the default group on load so only its tabs show, keeping the server-picked tab.
    const activeGroup = this.querySelector('.tab-collection-carousel-group.is-active') || this.groups[0];
    if (activeGroup) {
      const groupHandle = activeGroup.getAttribute('data-group');
      const activeTab = this.querySelector('.tab-collection-carousel-tab.is-active');
      const activeTabGroup = activeTab ? activeTab.getAttribute('data-group') : '';
      const keepTab = activeTab && (!activeTabGroup || activeTabGroup === groupHandle || groupHandle === '*');
      this.activateGroup(groupHandle, keepTab ? activeTab : null);
    } else {
      // Init the swiper for whichever panel is active by default (usually the first).
      const activePanel = this.querySelector('.tab-collection-carousel-panel.is-active');
      if (activePanel) this.initSwiperForPanel(activePanel);
    }

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', this._onBlockSelect);
    }
  }
 
  disconnectedCallback() {
    // Clean up listeners and destroy any Swiper instances so the theme
    // editor's "section:load" re-render doesn't leave orphaned instances
    // or throw "Swiper already initialized" on the same DOM node.
    this.tabs.forEach((tab) => tab.removeEventListener('click', this._onTabClick));
    this.groups.forEach((group) => group.removeEventListener('click', this._onGroupClick));
    document.removeEventListener('shopify:block:select', this._onBlockSelect);
 
    this.swipers.forEach((swiper) => {
      if (swiper && typeof swiper.destroy === 'function') {
        swiper.destroy(true, true);
      }
    });
    this.swipers.clear();
  }
 
  _onTabClick(event) {
    this.activateTab(event.currentTarget);
  }

  _onGroupClick(event) {
    this.activateGroup(event.currentTarget.getAttribute('data-group'));
  }

  // Theme editor: selecting a tab block shows that tab (and its group).
  _onBlockSelect(event) {
    if (this.contains(event.target) && event.target.classList.contains('tab-collection-carousel-tab')) {
      const group = event.target.getAttribute('data-group');
      if (group && this.groups.length) {
        this.activateGroup(group, event.target);
      } else {
        this.activateTab(event.target);
      }
    }
  }

  // Show only the tabs tied to this group (tabs without a group show in every group,
  // and the "all" group '*' shows every tab), then open the given tab, or the group's first tab.
  activateGroup(groupHandle, tabToActivate) {
    this.groups.forEach((group) => {
      const isActive = group.getAttribute('data-group') === groupHandle;
      group.classList.toggle('is-active', isActive);
      group.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    let firstTab = null;
    this.tabs.forEach((tab) => {
      const tabGroup = tab.getAttribute('data-group');
      const inGroup = !tabGroup || tabGroup === groupHandle || groupHandle === '*';
      tab.hidden = !inGroup;
      if (inGroup && !firstTab) firstTab = tab;
    });

    const tabList = this.querySelector('.tab-collection-carousel-tabs');
    if (tabList) tabList.scrollLeft = 0;

    const tab = tabToActivate || firstTab;
    if (tab) this.activateTab(tab);
  }
 
  activateTab(tab) {
    const targetId = tab.getAttribute('data-tab-target');
 
    this.tabs.forEach((t) => {
      t.classList.remove('is-active');
      t.setAttribute('aria-selected', 'false');
    });
    tab.classList.add('is-active');
    tab.setAttribute('aria-selected', 'true');

    // Each tab has its own "view all" link; show the active tab's one.
    this.tabLinks.forEach((link) => {
      link.hidden = link.getAttribute('data-tab-link') !== targetId;
    });
 
    this.panels.forEach((panel) => {
      if (panel.id === targetId) {
        panel.classList.add('is-active');
        panel.removeAttribute('hidden');
        this.initSwiperForPanel(panel);
      } else {
        panel.classList.remove('is-active');
        panel.setAttribute('hidden', '');
      }
    });
  }
 
  initSwiperForPanel(panel) {
    // Already inited: just recalculate, since the panel may have been
    // resized/hidden while inactive (widths, new slide count, etc.).
    if (this.swipers.has(panel)) {
      const existing = this.swipers.get(panel);
      // update() must run after the panel is visible, so defer one frame.
      requestAnimationFrame(() => existing.update());
      return;
    }
 
    const sliderEl = panel.querySelector('.swiper');
    if (!sliderEl || !window.Swiper) return;
 
    // Panel must be visible (not [hidden]) before Swiper measures it,
    // otherwise slide widths are calculated as 0.
    requestAnimationFrame(() => {
      const swiper = new window.Swiper(sliderEl, {
        slidesPerView: 2.5,
        centeredSlides: false,
        spaceBetween: 16,
        loop: false,
        autoHeight: false,
        // String selectors are automatically scoped to this swiper's own
        // container (sliderEl), so reusing the same classes across every
        // tab panel is safe - each instance only looks at its own children.
        // pagination: {
        //   el: '.swiper-pagination',
        //   clickable: true,
        // },
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        breakpoints: {
          744: {
            slidesPerView: 3.5,
            slidesPerGroup: 2,
          },
          1280: {
            spaceBetween: 24,
            slidesPerView: 4.5,
            slidesPerGroup: 1,
          },
        },
      });
 
      this.swipers.set(panel, swiper);
    });
  }
}
 
if (!customElements.get('tab-collection-carousel')) {
  customElements.define('tab-collection-carousel', TabCollectionCarousel);
}